/**
 * Every string of the PIN-on-Going flow (TECH-663).
 *
 * Where the sheet says what another join surface already says, it takes those words from
 * `spaceAccessCopy` (the join and PIN words) or `membershipFailureCopy` (the join failures), so
 * the Space page, the kit, a Space card and this sheet never disagree. The rest is the sheet's
 * own, passed through the canonical tone of voice in the PR review (what an answer promises is
 * Dexter's call: no timing, no outcome nobody controls). House rules: no exclamation marks, no
 * em dashes.
 *
 * A join failure that is not about the PIN (a server error, no connection, a Space gone) has no
 * line here: as on every other PIN field, the line under the field is about the PIN only, and
 * the failure is said once in a toast (`joinFailureToast`).
 */
import {
  PIN_PLACEHOLDER,
  PIN_REJECTED_ERROR,
  PIN_SUBMIT_LABEL,
  PIN_SUBMITTING_LABEL,
  REQUEST_ACCESS_LABEL,
  REQUEST_SENT_TOAST,
} from '@components/community/spaceAccessCopy';

export const goingTitle = "You're going";

/** What the person can do here, said first: the PIN button below does exactly this. */
export const joinIntro = 'Join the host Space to see posts from people going.';
export const hostedBy = 'Hosted by';

/** The PIN entry's words: the same constants as the Space page's PIN drawer. */
export const pinPlaceholder = PIN_PLACEHOLDER;
export const pinLabel = (space: string): string => `${space} PIN`;

export const join = PIN_SUBMIT_LABEL;
export const joining = PIN_SUBMITTING_LABEL;
export const requestAccess = REQUEST_ACCESS_LABEL;

/** The line under the PIN field for a wrong PIN: the same words as every PIN drawer. */
export const wrongPin = PIN_REJECTED_ERROR;
/** The sheet's own: only it turns a 429 into "use Request access". */
export const rateLimited = 'Too many tries. Request access instead.';

export const joined = (space: string): string => `You're in ${space}`;
/** The chip's words before the Space's name: the card renders the name apart, bidi-isolated. */
export const joinedChipLead = 'Joined';
export const joinedChip = (space: string): string => `${joinedChipLead} ${space}`;
/**
 * A right PIN on a Space whose host reviews every request (D5). It promises no timing and no
 * outcome: the host may take days, or decline. The second sentence is the "Request sent" toast's.
 */
export const pinPending = `PIN accepted. ${REQUEST_SENT_TOAST.description}`;
/**
 * A request that waits on the host: the "Request sent" toast's title, then who acts. "it", not
 * "your request" again, on a row that shows for 1.6 s. (`pinPending` keeps "your request": after
 * "PIN accepted", "it" would point at the PIN.)
 */
export const requested = `${REQUEST_SENT_TOAST.title}. The host will review it.`;
export const requestedChipLead = `${REQUEST_SENT_TOAST.title} to`;
export const requestedChip = (space: string): string => `${requestedChipLead} ${space}`;
/**
 * A PIN sent for a request that stopped waiting in that moment (409 JOIN_REQUEST_NOT_PENDING: a
 * host declined it, or it is gone). The fact only: the card has no Join, so the toast's way on
 * ("Join again to use your PIN.") is not said here. It names the Space like `joined` does.
 */
export const requestClosed = (space: string): string =>
  `Your request to join ${space} is no longer open`;

export const continueLabel = 'Continue';
export const ticketQuestion = 'Got your ticket?';
